import React, { useCallback, useMemo } from 'react';
import { DEAL_STATUS_DESCRIPTION, DEAL_STATUS_LABEL } from 'data/dealStatus';
import useMatchMedia from 'hooks/useMatchMedia';
import {
  ASKING_PRICE_SELECT,
  DEAL_OUTCOME,
  DEAL_STAGE,
  DealOutcome,
} from 'models/DealRoom';
import { LOCATION_STATE, NZ_STATE } from 'models/Listing';
import { useTranslation } from 'react-i18next';
import { INDUSTRY } from 'models/Industry';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';

export default function useOperationStepValues(fullNZ?: boolean) {
  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const createSelectOptions = useCreateSelectOptions();

  const locationOptions = useMemo(
    () => [
      { value: LOCATION_STATE.NSW, label: t('nsw') },
      { value: LOCATION_STATE.VIC, label: t('vic') },
      { value: LOCATION_STATE.QLD, label: t('qld') },
      { value: LOCATION_STATE.WA, label: t('wa') },
      { value: LOCATION_STATE.SA, label: t('sa') },
      { value: LOCATION_STATE.TAS, label: t('tas') },
      { value: LOCATION_STATE.ACT, label: t('act') },
      { value: LOCATION_STATE.NT, label: t('nt') },
      ...(fullNZ
        ? [{ value: NZ_STATE, label: t('nz') }]
        : [
            { value: LOCATION_STATE.NNZ, label: t('nnz') },
            { value: LOCATION_STATE.SNZ, label: t('snz') },
          ]),
    ],
    [fullNZ, t],
  );

  const industrySelectOptions = useMemo(
    () => createSelectOptions(Object.values(INDUSTRY)),
    [createSelectOptions],
  );

  const profitMeasureOptions = useMemo(
    () => [
      { label: t('EBITDA'), value: 'EBITDA' },
      { label: t('EBIT'), value: 'EBIT' },
      { label: t('PBT'), value: 'PBT' },
    ],
    [t],
  );

  const dealOutcomeOptions = useMemo(
    () => createSelectOptions(Object.values(DEAL_OUTCOME)),
    [createSelectOptions],
  );

  const priceGuideOptions = useMemo(
    () => [
      { label: t('fixedPrice'), value: ASKING_PRICE_SELECT.FIXED_PRICE },
      { label: t('priceRange'), value: ASKING_PRICE_SELECT.PRICE_RANGE },
      {
        label: t(isPhablet ? 'eoi' : 'expressionOfInterest'),
        value: ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST,
      },
    ],
    [isPhablet, t],
  );

  const getDealStageOptions = useCallback(
    (dealOutcome: DealOutcome) =>
      Object.values(DEAL_STAGE)
        .filter(
          (value) =>
            value !==
            (dealOutcome === DEAL_OUTCOME.SALE_CAMPAIGN
              ? DEAL_STAGE.COMPLETE
              : DEAL_STAGE.SOLD),
        )
        .map((value) => ({
          value,
          renderedOption: (
            <div>
              <p>{t(DEAL_STATUS_LABEL[value])}</p>
              {value !== DEAL_STAGE.ARCHIVED && (
                <p>{t(DEAL_STATUS_DESCRIPTION[value])}</p>
              )}
            </div>
          ),
        })),
    [t],
  );

  return {
    locationOptions,
    industrySelectOptions,
    profitMeasureOptions,
    dealOutcomeOptions,
    priceGuideOptions,
    getDealStageOptions,
  };
}
